/**
 * The organisation's people: who is in which department, as what, and who is
 * disabled. An admin adds, edits, disables and removes people here; everybody
 * else can see the list, because "who in IT do I ask" is a fair question.
 *
 * Adding somebody makes their account on the spot, so they can sign in at
 * once (decisions.md, "An organisation's admins add its people"). Disabling
 * somebody keeps them in the log and on their cards, which then show as
 * needing a new owner (PRD 6.4). Removing somebody hands their unfinished
 * work to a person chosen first, then takes them out of the organisation.
 */

import { useState, type FormEvent } from "react";
import {
  compareDates,
  isAway,
  type Member,
  type MembershipRole,
  type PeopleResponse,
  type PersonUpdateRequest,
  type PersonUpdateResponse,
} from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { dayText, ROLE_LABEL, ROLES } from "../lib/words.js";
import { PageHeader, Problem } from "./bits.js";

export function People({ selfId }: { selfId: string }) {
  const { org, people, departments, refreshPeople } = useOrg();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const admin = org.role === "admin";

  async function run(work: () => Promise<PeopleResponse>): Promise<boolean> {
    setBusy(true);
    setProblem(null);
    setConfirming(null);
    try {
      const answer: PersonUpdateResponse = await work();
      if (answer.confirming) setConfirming(answer.confirming);
      await refreshPeople();
      return true;
    } catch (error) {
      setProblem(sentence(error));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const active = people.filter((person) => person.active);
  const off = people.filter((person) => !person.active && !person.removed);
  const former = people.filter((person) => person.removed);

  return (
    <section className="page">
      <PageHeader title="People" meta="A person's department decides whose queue a card lands in." />
      <Problem>{problem}</Problem>
      {confirming ? (
        <p className="notice-line" role="status">
          A link is on its way to {confirming}. The address changes when they open it and sign in.
        </p>
      ) : null}

      {admin ? <AddPerson busy={busy} departments={departments} run={run} /> : null}

      <ul className="cards">
        {active.map((person) => (
          <PersonRow
            key={person.id}
            person={person}
            admin={admin}
            self={person.id === selfId}
            busy={busy}
            run={run}
          />
        ))}
      </ul>
      {off.length > 0 ? (
        <details className="panel zone-done">
          <summary>Disabled: {off.length}</summary>
          <ul className="cards">
            {off.map((person) => (
              <PersonRow key={person.id} person={person} admin={admin} self={false} busy={busy} run={run} />
            ))}
          </ul>
        </details>
      ) : null}
      {former.length > 0 ? (
        <details className="panel zone-done">
          <summary>Former members: {former.length}</summary>
          <p className="muted">
            They have left. Their name stays on the cards they worked and in the log. To bring somebody back,
            add the same address again.
          </p>
          <ul className="cards">
            {former.map((person) => (
              <PersonRow key={person.id} person={person} admin={false} self={false} busy={busy} run={run} />
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

/**
 * A department from the organisation's list, never typed: a typo used to make
 * a new department. A person already in one the list no longer offers (it
 * was archived) keeps it as a choice until somebody moves them.
 */
function DepartmentSelect({ value, onChange }: { value: string; onChange: (department: string) => void }) {
  const { departments } = useOrg();
  const choices = value && !departments.includes(value) ? [value, ...departments] : departments;
  return (
    <label className="field">
      Department
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">No department</option>
        {choices.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

function AddPerson({
  busy,
  departments,
  run,
}: {
  busy: boolean;
  departments: string[];
  run: (work: () => Promise<PeopleResponse>) => Promise<boolean>;
}) {
  const { api } = useOrg();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<MembershipRole>("member");
  const [department, setDepartment] = useState(departments[0] ?? "");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (await run(() => api.addPerson({ email, name, role, department }))) {
      setEmail("");
      setName("");
    }
  }

  return (
    <form className="panel admin-panel" onSubmit={submit}>
      <h2 className="admin-heading">Add somebody</h2>
      <p className="muted">They can sign in with this address straight away.</p>
      <div className="admin-form">
        <label className="field">
          Name
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ivan Petrov" />
        </label>
        <label className="field">
          Email address
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="ivan@harborhealth.com"
          />
        </label>
        <DepartmentSelect value={department} onChange={setDepartment} />
        <label className="field">
          Role
          <select value={role} onChange={(event) => setRole(event.target.value as MembershipRole)}>
            {ROLES.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABEL[option]}
              </option>
            ))}
          </select>
        </label>
        <button className="button" type="submit" disabled={busy || !email.trim()}>
          Add
        </button>
      </div>
    </form>
  );
}

type Panel = "edit" | "away" | "remove";

/**
 * One person. Their actions sit in a single row; the one chosen opens its
 * form beneath, so only one thing is ever being changed at a time.
 *
 * Out of office: until the day they are back, the notices for their cards go
 * to the card's backup, who can work it. The cards stay theirs. Their own to
 * set, and an admin's for them.
 */
function PersonRow({
  person,
  admin,
  self,
  busy,
  run,
}: {
  person: Member;
  admin: boolean;
  self: boolean;
  busy: boolean;
  run: (work: () => Promise<PeopleResponse>) => Promise<boolean>;
}) {
  const { api, people } = useOrg();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [name, setName] = useState(person.name);
  const [email, setEmail] = useState(person.email);
  const [role, setRole] = useState(person.role);
  const [department, setDepartment] = useState(person.department);
  const [until, setUntil] = useState("");
  const [handTo, setHandTo] = useState("");
  // Where this browser is: close enough to show who is away, and the worker decides for real.
  const today = new Date().toISOString().slice(0, 10);
  const away = isAway(person, today);
  const mayAway = person.active && (self || admin);
  const label = person.name || person.email;
  const close = () => setPanel(null);
  const open = (next: Panel) => {
    setName(person.name);
    setEmail(person.email);
    setRole(person.role);
    setDepartment(person.department);
    setUntil(person.awayUntil ?? "");
    setHandTo("");
    setPanel(next);
  };
  // Who can take their work: people who can receive it, their own department's first.
  const takers = people
    .filter((other) => other.active && other.id !== person.id)
    .sort(
      (a, b) =>
        Number(b.department === person.department) - Number(a.department === person.department) ||
        (a.name || a.email).localeCompare(b.name || b.email),
    );

  return (
    <li className={`card${person.active ? "" : " card-later"}`}>
      <div className="card-top">
        <h3 className="card-title">{label}</h3>
        {person.active ? (
          <span className="chip">{ROLE_LABEL[person.role]}</span>
        ) : (
          <span className="pill s-waiting">{person.removed ? "Removed" : "Disabled"}</span>
        )}
      </div>
      <p className="card-meta">
        {person.department ? <span className="chip">{person.department}</span> : <span>No department</span>}
        {person.name ? <span className="admin-wrap">{person.email}</span> : null}
        {self ? <span>You</span> : null}
        {away ? (
          <span className="pill s-waiting">Out of office until {dayText(person.awayUntil!)}</span>
        ) : null}
      </p>

      {panel === null && (admin || mayAway) ? (
        <div className="action-row">
          {admin ? (
            <button
              className="button button-quiet"
              type="button"
              disabled={busy}
              onClick={() => open("edit")}
            >
              Edit
            </button>
          ) : null}
          {mayAway ? (
            <button
              className="button button-quiet"
              type="button"
              disabled={busy}
              onClick={() => open("away")}
            >
              {away ? "Change return date" : "Set out of office"}
            </button>
          ) : null}
          {mayAway && away ? (
            <button
              className="button button-quiet"
              type="button"
              disabled={busy}
              onClick={() => void run(() => api.setAway(person.id, null))}
            >
              Back in the office
            </button>
          ) : null}
          {admin && !self ? (
            <button
              className="button button-quiet"
              type="button"
              disabled={busy}
              onClick={() => void run(() => api.updatePerson(person.id, { active: !person.active }))}
            >
              {person.active ? "Disable" : "Enable"}
            </button>
          ) : null}
          {admin && !self ? (
            <button
              className="button button-danger"
              type="button"
              disabled={busy}
              onClick={() => open("remove")}
            >
              Remove
            </button>
          ) : null}
        </div>
      ) : null}

      {panel === "edit" ? (
        <form
          className="admin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const change: PersonUpdateRequest = { name, role, department };
            if (email.trim().toLowerCase() !== person.email) change.email = email;
            if (await run(() => api.updatePerson(person.id, change))) close();
          }}
        >
          <label className="field">
            Name
            <input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="field">
            Email address
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <DepartmentSelect value={department} onChange={setDepartment} />
          <label className="field">
            Role
            <select
              value={role}
              disabled={self}
              onChange={(event) => setRole(event.target.value as MembershipRole)}
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {ROLE_LABEL[option]}
                </option>
              ))}
            </select>
          </label>
          <p className="muted">
            A new address is mailed a link, and changes when they open it and sign in. Their cards and history
            stay with them.
          </p>
          <div className="action-row">
            <button className="button" type="submit" disabled={busy || !email.trim()}>
              Save
            </button>
            <button className="button button-quiet" type="button" onClick={close}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {panel === "away" ? (
        <form
          className="admin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (await run(() => api.setAway(person.id, until))) close();
          }}
        >
          <label className="field">
            Back on
            <input type="date" value={until} min={today} onChange={(event) => setUntil(event.target.value)} />
          </label>
          <p className="muted">
            Until then, notices for {self ? "your" : "their"} cards go to each card&rsquo;s backup. The cards
            stay {self ? "yours" : "theirs"}.
          </p>
          <div className="action-row">
            <button
              className="button"
              type="submit"
              disabled={busy || !until || compareDates(until, today) <= 0}
            >
              Save
            </button>
            <button className="button button-quiet" type="button" onClick={close}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {panel === "remove" ? (
        <form
          className="admin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (await run(() => api.removePerson(person.id, handTo || null))) close();
          }}
        >
          <label className="field">
            Hand their work to
            <select value={handTo} onChange={(event) => setHandTo(event.target.value)}>
              <option value="">Nobody, they hold no work</option>
              {takers.map((other) => (
                <option key={other.id} value={other.id}>
                  {other.name || other.email}
                  {other.department ? `, ${other.department}` : ""}
                </option>
              ))}
            </select>
          </label>
          <p className="muted">
            Their unfinished cards and standing assignments move to this person, who is told. {label} is then
            removed from the organisation, and what they did stays in the log. To keep them here but stop them
            taking work, disable them instead.
          </p>
          <div className="action-row">
            <button className="button button-danger" type="submit" disabled={busy}>
              Remove {label}
            </button>
            <button className="button button-quiet" type="button" onClick={close}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
